import { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
  doc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';

const ALLOWED_TEAM_ROLES = new Set(['player', 'coach', 'assistant_coach', 'analyst']);

export const useOwnerTools = (auth, db, teamId, isOwner, ownerUid, setTeamMembers) => {
  const [joinRequests, setJoinRequests] = useState([]);
  const [joinRequestsLoading, setJoinRequestsLoading] = useState(false);
  const [joinRequestsError, setJoinRequestsError] = useState('');
  const [updatingRequestId, setUpdatingRequestId] = useState('');

  useEffect(() => {
    const loadJoinRequests = async () => {
      if (!isOwner || !teamId) {
        setJoinRequests([]);
        return;
      }
      setJoinRequestsLoading(true);
      setJoinRequestsError('');
      try {
        const snap = await getDocs(
          query(collection(db, 'joinRequests'), where('teamId', '==', teamId), orderBy('createdAt', 'desc'))
        );
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setJoinRequests(rows);
      } catch (err) {
        console.error('Failed to load join requests', err);
        setJoinRequestsError('Unable to load join requests.');
      } finally {
        setJoinRequestsLoading(false);
      }
    };
    loadJoinRequests();
  }, [db, isOwner, teamId]);

  const updateJoinRequestStatus = async (req, status) => {
    const user = auth?.currentUser;
    if (!user || !req?.id) return;
    setUpdatingRequestId(req.id);
    try {
      await updateDoc(doc(db, 'joinRequests', req.id), {
        status,
        decidedBy: user.uid,
        decidedAt: serverTimestamp(),
      });

      if (status === 'approved' && req.userId && req.teamId) {
        try {
          await updateDoc(doc(db, 'users', req.userId), {
            teamId: req.teamId,
            role: 'player',
          });
          await updateDoc(doc(db, 'teams', req.teamId), { memberIds: arrayUnion(req.userId) });
          if (setTeamMembers) {
            setTeamMembers((prev) =>
              prev.some((m) => m.id === req.userId)
                ? prev
                : [...prev, { id: req.userId, role: 'player', ...req }]
            );
          }
        } catch (err) {
          console.error('Failed to attach approved user to team', err);
        }
      }

      setJoinRequests((prev) =>
        status === 'rejected'
          ? prev.filter((r) => r.id !== req.id)
          : prev.map((r) => (r.id === req.id ? { ...r, status } : r))
      );
    } catch (err) {
      console.error('Failed to update join request', err);
      setJoinRequestsError('Could not update request. Try again.');
    } finally {
      setUpdatingRequestId('');
    }
  };

  const removeMemberFromTeam = async (member) => {
    const user = auth?.currentUser;
    if (!user || !member?.id || !teamId) return;
    if (ownerUid && member.id === ownerUid) {
      setJoinRequestsError('Subscription owner cannot be removed from team access.');
      return;
    }
    // lightweight confirmation to avoid accidental removals
    if (!window.confirm(`Remove ${member.ign || member.email || 'this user'} from the team?`)) return;
    try {
      await updateDoc(doc(db, 'users', member.id), { teamId: '' });
      await updateDoc(doc(db, 'teams', teamId), { memberIds: arrayRemove(member.id) });
      if (setTeamMembers) setTeamMembers((prev) => prev.filter((m) => m.id !== member.id));
    } catch (err) {
      console.error('Failed to remove member from team', err);
      setJoinRequestsError('Unable to remove member. Try again later.');
    }
  };

  const updateMemberRole = async (member, newRole) => {
    const safeRole = (newRole || '').toLowerCase();
    if (!member?.id || !teamId || !safeRole) return;
    if (!ALLOWED_TEAM_ROLES.has(safeRole)) return;

    try {
      await updateDoc(doc(db, 'users', member.id), { role: safeRole });
      if (setTeamMembers) {
        setTeamMembers((prev) =>
          prev.map((m) => (m.id === member.id ? { ...m, role: safeRole } : m))
        );
      }
    } catch (err) {
      console.error('Failed to update member role', err);
      setJoinRequestsError('Unable to update member role. Try again later.');
    }
  };

  const transferOwnership = async (fromMember, toMember) => {
    if (!auth?.currentUser || !teamId || !toMember?.id) return;

    const newOwnerId = toMember.id;
    const previousOwnerId = fromMember?.id && fromMember.id !== newOwnerId ? fromMember.id : null;

    try {
      // Update team owner field
      await updateDoc(doc(db, 'teams', teamId), { owner: newOwnerId, ownerId: newOwnerId });

      // Keep team role separate from subscription ownership
      await updateDoc(doc(db, 'users', newOwnerId), { isOwner: true });
      if (previousOwnerId) {
        const prevPayload = { isOwner: false };
        if ((fromMember?.role || '').toLowerCase() === 'owner') prevPayload.role = 'player';
        await updateDoc(doc(db, 'users', previousOwnerId), prevPayload);
      }

      // Reflect in local teamMembers state
      if (setTeamMembers) {
        setTeamMembers((prev) =>
          prev.map((m) => {
            if (m.id === newOwnerId) return { ...m, isOwner: true };
            if (previousOwnerId && m.id === previousOwnerId) return { ...m, isOwner: false };
            return m;
          })
        );
      }
    } catch (err) {
      console.error('Failed to transfer ownership', err);
      setJoinRequestsError('Unable to transfer ownership. Try again later.');
    }
  };

  return {
    joinRequests,
    joinRequestsLoading,
    joinRequestsError,
    updateJoinRequestStatus,
    updatingRequestId,
    removeMemberFromTeam,
    updateMemberRole,
    transferOwnership,
  };
};

export default useOwnerTools;
